import { z } from 'zod';
import { HOME_HORIZON_DEFAULT_DAYS } from '@shared/home/board-filters';
import type { ImpersonationSortFactsResponse, OwnerOption } from '@shared/schemas/admin-access';
import { readStoredJson } from '../../lib/readStoredJson';
import { formatRelativeTime } from '../account-view/timeline-helpers';

type SortFact = ImpersonationSortFactsResponse['people'][number];

const days = `${HOME_HORIZON_DEFAULT_DAYS} days`;

/** The ⌘I list's sort keys, in menu order. `dirs` names the default order, then the reverse. */
export const SORT_KEYS = {
  name: { label: 'Name', hint: 'A to Z', unit: '', dirs: ['A–Z', 'Z–A'] },
  nextMeetings: { label: 'Next meetings', hint: `How many in the next ${days}`, unit: `next ${days}`, dirs: ['Most first', 'Fewest first'] },
  lastMeeting: { label: 'Last meeting', hint: `Their latest, in the last ${days}`, unit: 'last meeting', dirs: ['Most recent', 'Least recent'] },
  lastActive: { label: 'Last active', hint: 'Their own last action in Terra', unit: 'last active', dirs: ['Most recent', 'Least recent'] },
} as const;

const SortChoiceSchema = z.object({
  key: z.enum(['name', 'nextMeetings', 'lastMeeting', 'lastActive']),
  reverse: z.boolean(),
});
export type SortChoice = z.infer<typeof SortChoiceSchema>;
export type SortKey = SortChoice['key'];
/** Menu order. */
export const SORT_KEY_ORDER: readonly SortKey[] = SortChoiceSchema.shape.key.options;

export const IMPERSONATION_SORT_KEY = 'terra:impersonation:sort';
const BY_NAME: SortChoice = { key: 'name', reverse: false };

/** The viewer's last sort, per browser; a sort is kept by picking it. */
export function readSortChoice(): SortChoice {
  return readStoredJson(localStorage, IMPERSONATION_SORT_KEY, SortChoiceSchema, BY_NAME);
}

export function saveSortChoice(choice: SortChoice): void {
  try {
    localStorage.setItem(IMPERSONATION_SORT_KEY, JSON.stringify(choice));
  } catch {
    // A per-browser convenience; an unsaved sort only resets next time.
  }
}

const time = (at: SortFact['lastMeetingAt'] | undefined) => (at ? new Date(at).getTime() : null);

function valueOf(key: Exclude<SortKey, 'name'>, fact: SortFact | undefined): number | null {
  if (!fact) return null;
  if (key === 'nextMeetings') return fact.nextMeetings;
  return time(key === 'lastMeeting' ? fact.lastMeetingAt : fact.lastActiveAt);
}

/**
 * The list in `choice`'s order. People with no value (no meeting, never active,
 * no Terra email) always come last, whichever way round; ties go A–Z.
 */
export function sortPeople(
  users: readonly OwnerOption[],
  choice: SortChoice,
  facts: ReadonlyMap<string, SortFact> | undefined,
  nameOf: (user: OwnerOption) => string,
): OwnerOption[] {
  const byName = (a: OwnerOption, b: OwnerOption) => nameOf(a).localeCompare(nameOf(b));
  if (choice.key === 'name') return [...users].sort((a, b) => (choice.reverse ? byName(b, a) : byName(a, b)));
  if (!facts) return [...users].sort(byName);
  const key = choice.key;
  return [...users].sort((a, b) => {
    const va = valueOf(key, facts.get(a.slackUserId));
    const vb = valueOf(key, facts.get(b.slackUserId));
    if (va === null || vb === null) return va === vb ? byName(a, b) : va === null ? 1 : -1;
    return (choice.reverse ? va - vb : vb - va) || byName(a, b);
  });
}

/** What a row shows for the sort in use; `muted` when they have nothing for it. */
export function sortValueOf(key: SortKey, fact: SortFact | undefined, now: Date): { text: string; muted: boolean } | null {
  if (key === 'name') return null;
  if (key === 'nextMeetings') {
    const n = fact?.nextMeetings ?? 0;
    return { text: String(n), muted: n === 0 };
  }
  const at = key === 'lastMeeting' ? fact?.lastMeetingAt : fact?.lastActiveAt;
  if (!at) return { text: key === 'lastMeeting' ? '—' : 'Never', muted: true };
  return { text: formatRelativeTime(String(at), now), muted: false };
}
